import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { css } from '@emotion/react';
import { PuzzlePiece, Square, Triangle } from '@phosphor-icons/react';
import { Button } from 'client/components/Button';
import { Dialog } from 'client/components/Dialog/Dialog';
import { Input } from 'client/components/Input';
import { useComponentIcon } from 'client/sections/components/ComponentIcon';
import { StackComponentDialogDescription } from './StackComponentDialogDescription';

export interface StackComponentDialogProps {
  component?: {
    identifier: string;
    version: string;
    type: 'component' | 'addon';
    details: {
      name: string;
      description: string;
      versions: string[];
      owner: {
        name: string;
        isVerified: boolean;
      };
      url: string;
    };
  } | null;
  onIdentifier: (identifier: string) => void;
  onVersion: (version: string) => void;
  onRemove: (url: string) => void;
  onClose: () => void;
}

export const StackComponentDialog = ({ component, onIdentifier, onVersion, onRemove, onClose }: StackComponentDialogProps) => {
  const imageUrl = useComponentIcon({ url: component?.details.url });
  const [identifier, setIdentifier] = useState('');
  const [version, setVersion] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!component) {
      setOpen(false);
      return;
    }
    if (open) return;
    setIdentifier(component.identifier || '');
    setVersion(component.version || '');
    setOpen(true);
  }, [open, component]);

  const handleIdentifierChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setIdentifier(event.target.value), [setIdentifier]);

  const handleRemove = useCallback(() => {
    if (!component) return;
    onRemove(component.details.url);
    onClose();
  }, [component?.details.url]);

  const handleSave = () => {
    if (!component) return;
    onVersion(version);
    onIdentifier(identifier.trim());
    setTimeout(() => onClose(), 100);
  };

  return (
    <Dialog
      open={open}
      title={component?.details.name || 'Component'}
      description={<StackComponentDialogDescription component={component} onVersion={onVersion} />}
      icon={<PuzzlePiece />}
      image={imageUrl ? { src: imageUrl, alt: 'component icon' } : null}
      iconOrnament={component?.type === 'component' ? <Square weight="bold" size={16} /> : <Triangle weight="bold" size={16} />}
      actions={
        <>
          <Button danger onClick={handleRemove}>
            Remove
          </Button>
          <div style={{ flex: 1 }} />
          <Button onClick={onClose}>Cancel</Button>
          <Button primary onClick={handleSave}>
            Save
          </Button>
        </>
      }
      onClose={onClose}
      css={dialogStyle}
    >
      <Input label="Identifier" value={identifier} onChange={handleIdentifierChange} />
    </Dialog>
  );
};

const dialogStyle = css`
  > .MuiDialog-container > .MuiPaper-root > .MuiDialogContent-root {
    display: flex;
  }
`;
